import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";

import { getServiceRequestPageRoute } from "../../../../routers/routeUtils";

import { CreateContractData } from "../../types/service.type";
import { useCreateContract } from "../../api/mutations/useCreateContract";
import { useGetServiceRequest } from "../../api/queries/useGetServiceRequest";
import { ServiceDetailsBackIcon } from "../../assets/icons/service-details-icons";
import { createContractContractDetailsSchema, createContractSchema } from "../../schemas/contract.schema";
import { useMinimumServicePrices } from "../../../../api/queries/payment/paymentQueries";

import ContractDetails from "./ContractDetails";
import MilestoneDetails from "./MilestoneDetails";
import ContractServiceSummary from "./ContractServiceSummary";

const createContractSteps = ["contractDetails", "milestoneDetails"] as const;

const getCurrentStepSchema = (step: "contractDetails" | "milestoneDetails") => {
  if (step === "contractDetails") {
    return createContractContractDetailsSchema;
  }
  return createContractSchema;
};

const CreateContractPage = () => {
  const [currentStep, setCurrentStep] = useState<(typeof createContractSteps)[number]>("contractDetails");
  const { t, i18n } = useTranslation("goService", { keyPrefix: "createContractPage" });
  const navigate = useNavigate();

  const { requestId } = useParams();

  const { mutate: createContract } = useCreateContract(Number(requestId));
  const { data: serviceRequest, isLoading: isServiceRequestLoading } = useGetServiceRequest(Number(requestId));
  const { minimumServicePrices } = useMinimumServicePrices();

  const methods = useForm<CreateContractData>({
    resolver: zodResolver(getCurrentStepSchema(currentStep)),
    defaultValues: {
      serviceRequestId: Number(requestId),
      description: undefined,
      milestones: [{ name: "", deliveryDueDate: "", paymentDueDate: "" }],
      paymentConfig: {
        amount: undefined,
        currency: undefined,
        type: undefined,
        paymentDueDate: undefined,
      },
    },
  });

  const handleBackToServiceRequest = () => {
    navigate(getServiceRequestPageRoute(requestId!, "contract"));
  };

  const handleBackButton = () => {
    if (currentStep === "milestoneDetails") {
      setCurrentStep("contractDetails");
    } else {
      handleBackToServiceRequest();
    }
  };

  const handleCreateContract = methods.handleSubmit(async (data) => {
    if (currentStep === "contractDetails") {
      let isValid = await methods.trigger();
      if (!isValid) return;

      const amount = methods.getValues("paymentConfig.amount");
      const currency = methods.getValues("paymentConfig.currency");
      if (amount && currency && minimumServicePrices) {
        const minimumServicePrice = minimumServicePrices[currency as keyof typeof minimumServicePrices];
        if (minimumServicePrice && amount < minimumServicePrice) {
          methods.setError("paymentConfig.amount", {
            message: "AMOUNT_MUST_BE_GREATER_THAN_MINIMUM_SERVICE_PRICE",
          });
          isValid = false;
        }
      }

      if (!isValid) return;
      setCurrentStep("milestoneDetails");
    } else {
      createContract(
        {
          ...data,
          paymentConfig: { ...data.paymentConfig, amount: data.paymentConfig.amount * 100 },
          serviceRequestId: Number(requestId),
        },
        {
          onSuccess: () => {
            navigate(getServiceRequestPageRoute(requestId!, "contract"));
          },
        }
      );
    }
  });

  useEffect(() => {
    if (serviceRequest) {
      methods.reset({
        serviceRequestId: Number(requestId),
        description: "",
        // milestones: [{ name: "", deliveryDueDate: "", paymentDueDate: "" }],
        milestones: [],
        paymentConfig: {
          amount: serviceRequest.service.paymentConfig.amount / 100,
          currency: serviceRequest.service.paymentConfig.currency,
          type: serviceRequest.service.paymentConfig.type,
          unit: serviceRequest.service.paymentConfig.unit,
        },
      });
    }
  }, [serviceRequest]);

  const error = methods.formState.errors;
  return (
    <FormProvider {...methods}>
      <Box sx={styles.container} component="form" onSubmit={handleCreateContract}>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.headerContainer}>
            <Typography sx={styles.title}>
              <IconButton size="small" onClick={handleBackToServiceRequest}>
                <ServiceDetailsBackIcon />
              </IconButton>
              {t("createContract")}
            </Typography>
          </Box>
          {currentStep === "contractDetails" && (
            <ContractDetails serviceRequest={serviceRequest} isLoading={isServiceRequestLoading} />
          )}
          {currentStep === "milestoneDetails" && <MilestoneDetails />}
          <Divider
            flexItem
            sx={{
              mx: "-20px",
            }}
          />
          <Box sx={styles.footerContainer}>
            <Button variant="outlined-md" onClick={handleBackButton} type="button">
              {currentStep === "contractDetails" ? t("cancel") : t("previous")}
            </Button>
            <Button variant="primary-md" type="submit">
              {currentStep === "contractDetails" ? t("next") : t("createContract")}
            </Button>
          </Box>
        </Box>
        <ContractServiceSummary serviceRequest={serviceRequest} isLoading={isServiceRequestLoading} />
      </Box>
    </FormProvider>
  );
};

export default CreateContractPage;

const styles = {
  container: {
    position: "relative",
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: "16px",
  },
  contentContainer: {
    width: { xs: "100%", lg: "553px" },
    display: "flex",
    flexDirection: "column",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "20px",
    gap: "20px",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  footerContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "12px",
  },
};
